/**
 * Gider pusulası kayıtları — `subeler/{sube}/giderPusulalari`.
 *
 * Kâğıt koçan matbaa baskılı ve kendi SERİ/SIRA numarasını taşıyor; resmi belge
 * o. Sistem burada koçandan kesilen pusulayı KAYDEDER: hangi alış için, kime,
 * ne kadar, hangi numarayla. Böylece aynı alışa ikinci pusula kesilmez ve
 * Kolaysoft yetkisi gelince aynı kayıt e-belgeye dönüştürülebilir.
 *
 * Belge numarası SERİ+SIRA'dan üretilir ve doküman kimliği olarak kullanılır:
 * aynı numara iki kez kaydedilemez.
 */

import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  orderBy,
  runTransaction,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const temiz = (v) => String(v ?? '').trim();

export const giderPusulalariRef = (sube) => collection(db, 'subeler', sube, 'giderPusulalari');

/** SERİ + SIRA → belge no ve doküman kimliği (B-998345). */
export const pusulaBelgeNo = (seri, sira) => {
  const s = temiz(seri).toUpperCase().replace(/[^A-Z0-9]/g, '');
  const n = temiz(sira).replace(/\D/g, '');
  return s && n ? `${s}-${n}` : '';
};

/** Tutarı yazıya çevirir (koçana elle yazarken kontrol için). */
export const tutarYaziyla = (tutar) => {
  const birler = ['', 'bir', 'iki', 'üç', 'dört', 'beş', 'altı', 'yedi', 'sekiz', 'dokuz'];
  const onlar = ['', 'on', 'yirmi', 'otuz', 'kırk', 'elli', 'altmış', 'yetmiş', 'seksen', 'doksan'];
  const uclu = (n) => {
    if (n === 0) return '';
    const y = Math.floor(n / 100);
    const k = n % 100;
    const yuz = y === 0 ? '' : y === 1 ? 'yüz' : `${birler[y]}yüz`;
    return `${yuz}${onlar[Math.floor(k / 10)]}${birler[k % 10]}`;
  };
  const tam = Math.floor(round2(tutar));
  if (tam === 0) return 'sıfır';
  const parcalar = [];
  const milyon = Math.floor(tam / 1000000);
  const bin = Math.floor((tam % 1000000) / 1000);
  const kalan = tam % 1000;
  if (milyon) parcalar.push(`${uclu(milyon)}milyon`);
  if (bin) parcalar.push(bin === 1 ? 'bin' : `${uclu(bin)}bin`);
  if (kalan) parcalar.push(uclu(kalan));
  return parcalar.join('');
};

/** Form → kayıt. Eksik alanlar burada yakalanır (sunucuya gitmeden). */
export const pusulaKaydiHazirla = (form, { sube, islem, personel }) => {
  const belgeNo = pusulaBelgeNo(form?.seri, form?.sira);
  if (!belgeNo) throw new Error('Seri ve sıra numarası zorunludur.');
  const satici = form?.satici || {};
  if (!temiz(satici.adSoyad)) throw new Error('Satıcının adı soyadı zorunludur.');
  const kalemler = (form?.kalemler || [])
    .map((k) => ({
      isinMahiyeti: temiz(k.isinMahiyeti),
      cinsi: temiz(k.cinsi),
      miktar: round2(k.miktar),
      birim: temiz(k.birim) || 'Adet',
      fiyat: round2(k.fiyat),
      tutar: round2(k.tutar)
    }))
    .filter((k) => k.tutar > 0 || k.isinMahiyeti || k.cinsi);
  if (!kalemler.length) throw new Error('En az bir satır gereklidir.');

  const toplam = round2(kalemler.reduce((t, k) => t + k.tutar, 0));
  const tev = form?.tevkifat || {};
  const gvTutar = round2(tev.gelirVergisiTutari);
  const fonPayi = round2(tev.fonPayi);
  const kesinti = round2(gvTutar + fonPayi);

  return {
    belgeNo,
    seri: temiz(form.seri).toUpperCase(),
    sira: temiz(form.sira).replace(/\D/g, ''),
    tarih: temiz(form.tarih),
    sube: temiz(sube),
    islemId: temiz(islem?.id),
    islemNo: temiz(islem?.islemNo),
    satici: {
      adSoyad: temiz(satici.adSoyad),
      tcKimlikNo: temiz(satici.tcKimlikNo).replace(/\D/g, ''),
      adres: temiz(satici.adres),
      vergiDairesi: temiz(satici.vergiDairesi),
      vergiNo: temiz(satici.vergiNo).replace(/\D/g, '')
    },
    kalemler,
    toplam,
    tevkifat: {
      gelirVergisiOrani: round2(tev.gelirVergisiOrani),
      gelirVergisiTutari: gvTutar,
      fonPayi,
      kesintiToplami: kesinti
    },
    netTutar: round2(toplam - kesinti),
    tutarYaziyla: tutarYaziyla(round2(toplam - kesinti)),
    /** kagit-kocan | e-gider-pusulasi (Kolaysoft yetkisi gelince) */
    kaynak: 'kagit-kocan',
    createdByPersonel: personel || null
  };
};

/**
 * Pusulayı kaydeder. Aynı SERİ-SIRA ikinci kez yazılamaz; aynı işleme ikinci
 * pusula da kesilemez (ikisi de tek işlemde kontrol edilir).
 */
export async function createGiderPusulasi({ sube, form, islem, personel }) {
  if (!sube) throw new Error('Şube bilgisi zorunludur.');
  const kayit = pusulaKaydiHazirla(form, { sube, islem, personel });
  const ref = doc(giderPusulalariRef(sube), kayit.belgeNo);

  await runTransaction(db, async (tx) => {
    const mevcut = await tx.get(ref);
    if (mevcut.exists()) {
      const err = new Error(`${kayit.belgeNo} numaralı gider pusulası zaten kayıtlı.`);
      err.code = 'DUPLICATE_BELGE_NO';
      throw err;
    }
    tx.set(ref, { ...kayit, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  });
  return { id: kayit.belgeNo, ...kayit };
}

/** Kesilmiş pusulalar (canlı). */
export function subscribeGiderPusulalari(sube, onData, onError) {
  const q = query(giderPusulalariRef(sube), orderBy('tarih', 'desc'));
  return onSnapshot(q, (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (err) => onError?.(err));
}

export async function fetchGiderPusulalari(sube) {
  const snap = await getDocs(query(giderPusulalariRef(sube), orderBy('tarih', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/*
 * ELLE "KESILDI" ISARETI — `subeler/{sube}/giderPusulasiIsaretleri/{islemId}`.
 *
 * Pusulalar kagit kocandan kesilmeye devam ediyor (02.10.2026, muhasebe:
 * Kolaysoft'tan simdilik kesilemiyor). Personel kocandan kestigi alisi listede
 * isaretler; boylece kesilen/kesilmeyen takibi yapilir. Kayit silinmez, isaret
 * kaldirilinca `kesildi: false` olur ve her degisiklik `gecmis`e yazilir.
 */
export const giderPusulasiIsaretleriRef = (sube) => collection(db, 'subeler', sube, 'giderPusulasiIsaretleri');

const personelOzeti = (personel) =>
  personel
    ? { id: personel.id || null, isim: personel.isim || '', soyisim: personel.soyisim || '' }
    : { id: null, isim: '', soyisim: '' };

export async function setGiderPusulasiKesildi({ sube, islem, kesildi, personel }) {
  if (!sube) throw new Error('Şube bilgisi zorunludur.');
  const islemId = temiz(islem?.id);
  if (!islemId) throw new Error('İşlem bilgisi eksik.');
  const kim = personelOzeti(personel);
  await setDoc(
    doc(giderPusulasiIsaretleriRef(sube), islemId),
    {
      islemId,
      islemNo: temiz(islem?.islemNo),
      sube,
      kesildi: Boolean(kesildi),
      updatedBy: kim,
      updatedAt: serverTimestamp(),
      gecmis: arrayUnion({ kesildi: Boolean(kesildi), at: new Date().toISOString(), by: kim })
    },
    { merge: true }
  );
}

/** Isaretler (canli): { [islemId]: { kesildi, updatedBy, updatedAt } } */
export function subscribeGiderPusulasiIsaretleri(sube, onData, onError) {
  return onSnapshot(
    giderPusulasiIsaretleriRef(sube),
    (snap) => {
      const harita = {};
      snap.forEach((d) => {
        harita[d.id] = d.data() || {};
      });
      onData(harita);
    },
    (err) => onError?.(err)
  );
}
